import useAccountConfigQuery from "../../hooks/data/queries/useAccountConfigQuery";
import AdyenConfigurations from "./AdyenConfigurations";
import CompanyContact from "./CompanyContact";
import Locations from "./Locations";
import MerchantConfigurations from "./MerchantConfigurations";
import MerchantUsers from "./MerchantUsers";
import UserNotes from "./UserNotes";
import RatePlans from "./RatePlans";
import Button from "../../components/Button";
import Spinner from "../../components/Spinner";
import Error from "../../components/Error";
import { createContext, useState } from "react";
import useCurrentUserAccount from "../../hooks/data/queries/useCurrentUserAccount";
import HasAccess, {
  csrAdminRoles,
  csrRoles,
  kycRoles,
} from "../../components/HasAccess";
import { useParams } from "react-router-dom";
import useOnboardingUrlQuery from "../../hooks/data/queries/useOnboardingUrlQuery";
import EmailSubscription from "./EmailSubscription";
import useCreateAccountHolderNotesMutation from "../../hooks/data/mutations/useCreateAccountHolderNotesMutation";
import useOnboardingUrlBalanceQuery from "../../hooks/data/queries/useOnboardingUrlBalanceQuery";
import useThemeIdByMerchantAccountId from "../../hooks/data/queries/useThemeIdByMerchantAccountId";
import AccountCreditDebitModal from "./AccountCreditDebitModal";
import CapitalInfo from "./CapitalInfo";
import Terminals from "./Terminals";
import EditClientNameModal from "./EditCompanyNameModal";
import Wallets from "./Wallets";
import { RatePlansProvider } from "../../hooks/data/providers/ratePlansProvider";

type CustomerSettingsRouteParams = {
  accountHolderCode: string;
};

type CustomerSettingsContextValue = {
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
};

export const CustomerSettingsContext =
  createContext<CustomerSettingsContextValue>({
    isEditing: false,
    setIsEditing: () => {},
  });

function CustomerSettings(): JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const { accountHolderCode: accountHolderCodeRouteParam } =
    useParams<CustomerSettingsRouteParams>();

  const { data: currentUserAccount } = useCurrentUserAccount();

  const accountHolderCode = accountHolderCodeRouteParam
    ? accountHolderCodeRouteParam
    : currentUserAccount?.accountHolderCode ?? "";

  const {
    data: accountConfigData,
    isLoading: accountConfigIsLoading,
    isSuccess: accountConfigDataSuccess,
    error: accountConfigDataError,
  } = useAccountConfigQuery(accountHolderCode, {
    enabled: Boolean(accountHolderCode),
  });

  const inBalance = Boolean(accountConfigData?.inBalancePlatform ?? false);
  const isMigrated = Boolean(accountConfigData?.migrated ?? false);
  const isCorpEdition =
    (accountConfigData?.edition ?? "").toLowerCase() === "corp";

  const { data: themeIdData } = useThemeIdByMerchantAccountId(
    accountConfigData?.merchantAccountId ?? "",
    {
      enabled: inBalance,
    }
  );

  const { mutateAsync: createNote } = useCreateAccountHolderNotesMutation();

  const [onboardingUrlQueryEnabled, setOnboardingUrlQueryEnabled] =
    useState<boolean>(false);

  const {
    isFetching: onboardingUrlClassicIsFetching,
    isSuccess: onboardingUrlClassicIsSuccess,
    isError: onboardingUrlClassicIsError,
  } = useOnboardingUrlQuery(
    {
      accountHolderCode: accountHolderCode,
      returnUrl:
        (process.env.REACT_APP_BASE_URI || window.location.origin) +
        "customer-settings/" +
        accountHolderCode,
    },
    {
      enabled: onboardingUrlQueryEnabled && !inBalance && !isMigrated,
      async onSuccess({ resultCode, redirectUrl }) {
        if (resultCode === "Success") {
          await createNote({
            accountHolderCode: accountHolderCode,
            freeText: "Client KYC information accessed",
          });

          window.location.href = redirectUrl;
        }
      },
      onError() {
        setOnboardingUrlQueryEnabled(false);
      },
    }
  );

  const {
    isFetching: onboardingUrlBalanceIsFetching,
    isSuccess: onboardingUrlBalanceIsSuccess,
    isError: onboardingUrlBalanceIsError,
  } = useOnboardingUrlBalanceQuery(
    {
      returnUrl:
        (process.env.REACT_APP_BASE_URI || window.location.origin) +
        "customer-settings/" +
        accountHolderCode,
      legalEntityId: accountConfigData?.legalEntityId ?? "",
      themeId: themeIdData || "",
    },
    {
      enabled: onboardingUrlQueryEnabled && (inBalance || isMigrated),
      async onSuccess({ redirectUrl }) {
        await createNote({
          accountHolderCode: accountHolderCode,
          freeText: "Client KYC information accessed",
        });

        window.location.href = redirectUrl;
      },
      onError() {
        setOnboardingUrlQueryEnabled(false);
      },
    }
  );

  const [accountCreditDebitModalIsOpen, setaccountCreditDebitModalIsOpen] =
    useState(false);
  const [editCompanyNameModalIsOpen, setEditCompanyNameModalIsOpen] =
    useState(false);

  if (!accountConfigDataSuccess && !accountConfigIsLoading) {
    return <Error message={accountConfigDataError || ""} />;
  }

  const isAdminOrCSR = currentUserAccount?.role
    ? csrAdminRoles.includes(currentUserAccount.role)
    : false;
  const isRestricted =
    accountConfigData?.merchantStatus &&
    accountConfigData.merchantStatus === "Restricted";

  return (
    <CustomerSettingsContext.Provider value={{ isEditing, setIsEditing }}>
      <div className="flex flex-col px-6">
        <div>
          <h1 className="mb-1 text-3xl">Client Settings</h1>
        </div>
        <div className="flex flex-col justify-between sm:flex-row sm:items-center">
          <div className="my-2 flex items-center gap-x-4 sm:mb-4">
            <h2 className="text-2xl text-slate-500">
              {accountConfigData?.companyName}
            </h2>
            {accountConfigDataSuccess && (
              <HasAccess allowedRoles={[...csrRoles]}>
                <Button
                  size="sm"
                  onClick={() => setEditCompanyNameModalIsOpen(true)}
                  variant="secondary"
                  disabled={accountConfigIsLoading}
                >
                  Edit
                </Button>
              </HasAccess>
            )}
          </div>
          {accountConfigData?.onboardingStatus !== "In Progress" && (
            <>
              <div className="my-2 flex gap-x-4 sm:my-0">
                {inBalance && !isCorpEdition && (
                  <HasAccess allowedRoles={[...csrAdminRoles]}>
                    <Button
                      onClick={() => setaccountCreditDebitModalIsOpen(true)}
                      size="sm"
                    >
                      Account Credit/Debit
                    </Button>
                  </HasAccess>
                )}
                {(isAdminOrCSR || !isRestricted) && (
                  <HasAccess allowedRoles={[...kycRoles]}>
                    <Button
                      custom-id="updateKycButton"
                      onClick={() => setOnboardingUrlQueryEnabled(true)}
                      size="sm"
                      className="flex items-center justify-center gap-x-1 self-center"
                      disabled={
                        accountConfigIsLoading ||
                        onboardingUrlClassicIsSuccess ||
                        onboardingUrlBalanceIsSuccess
                      }
                      icon={
                        accountConfigIsLoading ||
                        onboardingUrlClassicIsFetching ||
                        (onboardingUrlBalanceIsFetching && (
                          <Spinner className="mt-0.5 mr-1 text-gray-400" />
                        ))
                      }
                    >
                      Update KYC
                    </Button>
                  </HasAccess>
                )}
              </div>
              {(onboardingUrlClassicIsError || onboardingUrlBalanceIsError) && (
                <HasAccess allowedRoles={[...kycRoles]}>
                  <Error message="An error has occured. Please try again." />
                </HasAccess>
              )}
            </>
          )}
        </div>
        <div>
          <div className="flex flex-wrap gap-4 lg:grid lg:grid-cols-12 lg:grid-rows-[auto_auto]">
            <AdyenConfigurations
              accountConfigData={accountConfigData}
              isLoading={accountConfigIsLoading}
            />
            <div className="col-span-4">
              <CompanyContact
                accountHolderCode={accountHolderCode}
                firstName={accountConfigData?.contactFirstName ?? ""}
                lastName={accountConfigData?.contactLastName ?? ""}
                email={accountConfigData?.companyEmail ?? ""}
                isLoading={accountConfigIsLoading}
              />
              <HasAccess allowedRoles={[...csrRoles]}>
                <EmailSubscription
                  accountHolderCode={accountHolderCode}
                  dailySubscription={
                    accountConfigData?.dailySubscription ?? false
                  }
                  monthlySubscription={
                    accountConfigData?.monthlySubscription ?? false
                  }
                  isLoading={accountConfigIsLoading}
                  isCorpEdition={isCorpEdition}
                />
              </HasAccess>
            </div>
            <div className="w-full lg:col-span-4">
              {!isCorpEdition && (
                <Locations
                  renderMe={
                    accountConfigData?.onboardingStatus !== "In Progress"
                  }
                  accountHolderCode={accountHolderCode}
                  accountHolderId={accountConfigData?.id ?? ""}
                  locations={accountConfigData?.accounts ?? []}
                  migrated={accountConfigData?.migrated ?? false}
                  isLoading={accountConfigIsLoading}
                />
              )}
            </div>
            <div className="w-full lg:col-span-3">
              {(accountConfigData?.country === "US" ||
                accountConfigData?.country === "PR") &&
                accountConfigData?.edition.toLowerCase() !== "care" &&
                !isCorpEdition && (
                  <HasAccess allowedRoles={[...csrRoles]}>
                    <CapitalInfo
                      accountHolderCode={
                        accountConfigData?.adyenAccountHolderCode ?? ""
                      }
                      grantStatus={accountConfigData?.grantStatus ?? null}
                      offerStatus={accountConfigData?.offerStatus ?? null}
                      isLoading={accountConfigIsLoading}
                    />
                  </HasAccess>
                )}
            </div>
            <div className="w-full lg:col-span-4">
              {!isCorpEdition && (
                <Wallets
                  accountHolderCode={accountHolderCode}
                  accountHolderId={accountConfigData?.id ?? ""}
                  accountHolderRatePlans={accountConfigData?.ratePlans ?? []}
                  country={accountConfigData?.country ?? ""}
                  isLoading={accountConfigIsLoading}
                  isAdminOrCSR={isAdminOrCSR}
                />
              )}
            </div>
            <MerchantConfigurations
              accountHolderCode={accountHolderCode}
              accountHolderId={accountConfigData?.id ?? ""}
              country={accountConfigData?.country ?? ""}
              merchantConfigData={
                accountConfigData
                  ? {
                      merchantStatus: accountConfigData.merchantStatus,
                      useInterchangeRate: accountConfigData.useInterchangeRate,
                      shopperStatement:
                        accountConfigData.stores.at(0)?.shopperStatement ?? "",
                      techFeeRate: accountConfigData.techFeeRate,
                      convFee: accountConfigData.convFee,
                      convFeeRate: accountConfigData.convFeeRate,
                      timeZoneCode: accountConfigData.timeZoneCode,
                      useTransAmountLimit:
                        accountConfigData.useTransAmountLimit,
                      transAmountLimit: accountConfigData.transAmountLimit,
                      useDailyTransAmountLimit:
                        accountConfigData.useDailyTransAmountLimit,
                      dailyTransAmountLimit:
                        accountConfigData.dailyTransAmountLimit,
                      useACHTransAmountLimit:
                        accountConfigData.useACHTransAmountLimit,
                      achTransAmountLimit:
                        accountConfigData.achTransAmountLimit,
                      useACHDailyTransAmountLimit:
                        accountConfigData.useACHDailyTransAmountLimit,
                      achDailyTransAmountLimit:
                        accountConfigData.achDailyTransAmountLimit,
                      useTechFee: accountConfigData.useTechFee,
                      useConvFee: accountConfigData.useConvFee,
                      inBalancePlatform: accountConfigData.inBalancePlatform,
                      migrated: accountConfigData.migrated,
                      stores: accountConfigData.stores,
                      duplicateDetectionMinutes:
                        accountConfigData.duplicateDetectionMinutes,
                      corpAccountHolderId:
                        accountConfigData.corpAccountHolderId ?? "",
                      corpFee: accountConfigData.corpFee ?? "",
                      corpFeeDescription:
                        accountConfigData.corpFeeDescription ?? "",
                      useCorpFee: accountConfigData.useCorpFee ?? false,
                    }
                  : undefined
              }
              useInterchangeRate={
                accountConfigData?.useInterchangeRate ?? false
              }
              useTechFee={accountConfigData?.useTechFee ?? false}
              useConvFee={accountConfigData?.useConvFee ?? false}
              isAdminOrCSR={isAdminOrCSR}
              isLoading={accountConfigIsLoading}
              isCorpEdition={isCorpEdition}
              merchantAccountId={accountConfigData?.merchantAccountId ?? ""} // NEW
            />
            {!isCorpEdition && (
              <RatePlansProvider
                initialRatePlans={accountConfigData?.ratePlans ?? []}
                initialRatePlanType={
                  accountConfigData?.useInterchangeRate
                    ? "Interchange++"
                    : "Flat Rate Plan"
                }
                country={accountConfigData?.country ?? ""}
              >
                <RatePlans
                  accountHolderCode={accountHolderCode}
                  accountHolderId={accountConfigData?.id ?? ""}
                  accountHolderRatePlans={accountConfigData?.ratePlans ?? []}
                  useInterchangeRate={
                    accountConfigData?.useInterchangeRate ?? false
                  }
                  hasMonthlyFee={accountConfigData?.hasMonthlyFee ?? false}
                  monthlyFee={accountConfigData?.monthlyFee ?? "0"}
                  isMasterSurchargeEnabled={
                    accountConfigData?.useConvFee ?? false
                  }
                  inBalancePlatform={
                    accountConfigData?.inBalancePlatform ?? false
                  }
                  useTechFee={accountConfigData?.useTechFee ?? false}
                  useConvFee={accountConfigData?.useConvFee ?? false}
                  country={accountConfigData?.country ?? ""}
                  isLoading={accountConfigIsLoading}
                />
              </RatePlansProvider>
            )}
            <MerchantUsers
              accountHolderCode={accountHolderCode}
              accountConfigData={accountConfigData}
            />
            <HasAccess allowedRoles={[...csrRoles]}>
              {!isCorpEdition && (
                <Terminals
                  accountHolderCode={
                    accountConfigData?.adyenAccountHolderCode ?? ""
                  }
                  terminals={accountConfigData?.stores.at(0)?.terminals ?? []}
                  isLoading={accountConfigIsLoading}
                />
              )}
              <UserNotes
                accountHolderCode={accountHolderCode}
                timeZoneCode={accountConfigData?.timeZoneCode ?? ""}
              />
            </HasAccess>
          </div>
        </div>
      </div>
      {inBalance && !isCorpEdition && (
        <HasAccess allowedRoles={[...csrRoles]}>
          <AccountCreditDebitModal
            isOpen={accountCreditDebitModalIsOpen}
            onClose={() => setaccountCreditDebitModalIsOpen(false)}
            accountHolderCode={accountHolderCode}
            merchantAccountId={accountConfigData?.merchantAccountId ?? ""}
            primaryAccountAccountId={
              accountConfigData?.accounts.find((x) => x.primaryAccount)
                ?.balancePlatformAccountId ?? ""
            }
            accounts={accountConfigData?.accounts ?? []}
          />
        </HasAccess>
      )}
      <HasAccess allowedRoles={[...csrRoles]}>
        <EditClientNameModal
          isOpen={editCompanyNameModalIsOpen}
          onClose={() => setEditCompanyNameModalIsOpen(false)}
          accountHolderCode={accountHolderCode}
        />
      </HasAccess>
    </CustomerSettingsContext.Provider>
  );
}

export default CustomerSettings;
